import { ParagraphElement, SlateElement, SlateMark, SlateNode } from '@all-hands-hq/plex-graphql-schema';
import { toggleList, unwrapList, unwrapNodesByType } from '@udecode/slate-plugins';
import { Ancestor, Editor, Location, Node, NodeEntry, Path, Point, Range, Text, Transforms } from 'slate';

import { SlatePluginOptions } from './plugins';

export type Format = NonNullable<SlateNode['type']>;
export type HeadingStyle = NonNullable<ParagraphElement['namedStyleType']>;

export function isMarkActive(editor: Editor, format: SlateMark): boolean {
  const marks = Editor.marks(editor);
  return marks ? marks[format] === true : false;
}

export function isBlockActive(
  editor: Editor,
  { format, headingStyle }: { format: Format; headingStyle?: HeadingStyle }
): boolean {
  // TODO(https://github.com/all-hands-hq/source/issues/1046): use `Editor.nodes<SlateElement>`
  const [match] = Editor.nodes(editor, {
    match: (n) => n.type === format,
  });

  if (headingStyle == null || match == null) {
    return !!match;
  } else {
    const node = (match[0] as unknown) as SlateNode;
    return node.type === 'paragraph' && (node.namedStyleType || 'NORMAL_TEXT') === headingStyle;
  }
}

export function toggleMark(editor: Editor, format: SlateMark): void {
  const isActive = isMarkActive(editor, format);

  if (isActive) {
    Editor.removeMark(editor, format);
  } else {
    Editor.addMark(editor, format, true);
  }
}

export function selectNextNode(editor: Editor): void {
  if (editor.selection != null) {
    const after = Editor.after(editor, editor.selection);
    if (after != null) {
      editor.selection = { anchor: after, focus: after };
    }
  }
}

export function toggleBlock(
  editor: Editor,
  { format, headingStyle }: { format: Format; headingStyle?: HeadingStyle }
): void {
  const isActive = isBlockActive(editor, { format, headingStyle });
  const shouldToggleList = format === 'bulleted-list' || format === 'numbered-list';
  const shouldToggleHeading = headingStyle != null;
  const shouldToggleQuote = format === 'block-quote';

  // Clear existing block if necessary

  if (!shouldToggleList || (shouldToggleList && isActive)) {
    // Clear list
    unwrapList(editor, SlatePluginOptions);
  }

  if (!shouldToggleHeading || (shouldToggleHeading && isActive)) {
    // Clear headings
    Transforms.setNodes(
      editor,
      {
        namedStyleType: undefined,
      },
      { match: (node) => node.type === 'paragraph' }
    );
  }

  if (!shouldToggleQuote || (shouldToggleQuote && isActive)) {
    // Clear block-quote
    unwrapNodesByType(editor, ['block-quote']);
  }

  // Apply new block

  if (!isActive) {
    if (shouldToggleList) {
      toggleList(editor, { typeList: format, ...SlatePluginOptions });
    }

    if (shouldToggleHeading || shouldToggleQuote) {
      Transforms.setNodes(editor, {
        type: 'paragraph',
        ...(isActive ? { namedStyleType: undefined } : headingStyle != null ? { namedStyleType: headingStyle } : {}),
      });

      if (shouldToggleQuote) {
        const block = { type: format, children: [] };
        Transforms.wrapNodes(editor, block);
      }
    }
  }
}

/**
 * Check if the current node is empty.
 *
 * The difference between this and `Editor.isEmpty` is that this will return `true` if we have a node with multiple empty text nodes.
 */
export function isEmpty(editor: Editor, node: Ancestor): boolean {
  return node.children.every((child) => Text.isText(child) && child.text === '') && !Editor.isVoid(editor, node);
}

/**
 * Return a path pointing to the next line after the cursor is placed.
 * If no cursor is present, point to the end of the document.
 *
 * Note that this can perform modifications on the editor if `replaceEmptyLine` is `true`
 */
export function getVoidElementInsertLocation(
  editor: Editor,
  { startingAt, replaceEmptyLine }: { startingAt?: Location; replaceEmptyLine?: boolean }
): Path {
  // Find the block where we should insert
  const [targetBlock, targetBlockPath]: NodeEntry<Node> = Editor.above(editor, {
    match: (n) => Editor.isBlock(editor, n),
    at: startingAt,
  }) || [editor, Editor.end(editor, []).path];

  // If current block is empty, remove it and insert at this location, otherwise insert after this block
  if (replaceEmptyLine && isEmpty(editor, targetBlock)) {
    Transforms.removeNodes(editor, { at: targetBlockPath });
    return targetBlockPath;
  } else {
    return Path.next(targetBlockPath);
  }
}

export function insertAndSelectElements(
  editor: Editor,
  nodes: SlateNode[],
  { startingAt, replaceEmptyLine }: { startingAt?: Location; replaceEmptyLine?: boolean }
): void {
  const insertionPath = getVoidElementInsertLocation(editor, { startingAt, replaceEmptyLine });

  Editor.withoutNormalizing(editor, () => {
    Transforms.insertNodes(editor, nodes as Node[], {
      at: insertionPath,
      select: true,
    });

    // Selection for more than 1 element doesn't work within `insertNodes` so needs to be done manually
    if (nodes.length > 1) {
      Transforms.move(editor, {
        distance: nodes.length - 1,
        unit: 'line',
        edge: 'focus',
      });
    }
  });
}

export function updateElements<Before extends SlateElement, After extends SlateElement>({
  editor,
  match,
  map,
}: {
  editor: Editor;
  match: (element: SlateElement) => element is Before;
  map: (before: Before) => Omit<After, 'children' | 'text'>;
}): void {
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  const nodes: Iterable<NodeEntry<Before>> = Editor.nodes(editor, {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    match,
  });

  Editor.withoutNormalizing(editor, () => {
    for (const [node, path] of nodes) {
      const oldPropsAsUndefined = Object.fromEntries(
        Object.keys(node)
          .filter((key) => key !== 'children' && key !== 'text')
          .map((key) => [key, undefined])
      );
      Transforms.setNodes(editor, { ...oldPropsAsUndefined, ...map(node) }, { at: path });
    }
  });
}

/**
 * Check if the range is inside the same block, meaning it contains only inline elements inside it.
 */
export function isContinuousInline(editor: Editor, range: Range): boolean {
  const anchorBlockEntry = Editor.above(editor, { at: range.anchor, match: (n) => Editor.isBlock(editor, n) });
  const focusBlockEntry = Editor.above(editor, { at: range.focus, match: (n) => Editor.isBlock(editor, n) });

  return anchorBlockEntry != null && focusBlockEntry != null && anchorBlockEntry[0] === focusBlockEntry[0];
}

export function isValidPoint(editor: Editor, point: Point): boolean {
  try {
    return Editor.node(editor, point) != null;
  } catch (e) {
    return false;
  }
}

/**
 * Check if the range is valid given the current editor children
 */
export function isValidRange(editor: Editor, range: Range): boolean {
  return isValidPoint(editor, range.anchor) && isValidPoint(editor, range.focus);
}
